import { string, tuple, debug, pointTest, V4 } from '@moleculeone/utils';

import * as Drawing from './const';
import { atomToNeighborsPairs, neighborsList } from './selectors';
import { neighborsChiralityDataTest, molTest, positionsTest } from './checks';

export function chiralDeterminant(centerIdx, centerPos, nborsChiralityData, hydrogenIdx = null) {
  debug(string, centerIdx);
  debug(pointTest, centerPos);
  debug(neighborsChiralityDataTest, nborsChiralityData);

  let averageBondLength = 0;
  for (const { pos } of Object.values(nborsChiralityData)) {
    averageBondLength += V4.dist(pos, centerPos);
  }
  averageBondLength /= Object.values(nborsChiralityData).length;

  let bondVectorsSum = V4.zerovect;
  const vertices = [];
  for (const [atomIdx, { pos, type }] of Object.entries(nborsChiralityData)) {
    let z = 0;
    if (type === Drawing.WEDGE) {
      z = averageBondLength;
    }
    if (type === Drawing.DASHED) {
      z = -averageBondLength;
    }
    const bondVector = V4.add(V4.sub(pos, centerPos), V4.vect(0, 0, z));
    bondVectorsSum = V4.add(bondVectorsSum, bondVector);
    vertices.push({ idx: atomIdx, pos: bondVector });
  }

  if (vertices.length !== 4 && vertices.length !== 3) {
    return null;
  }
  if (vertices.length === 3) {
    const hIdx = hydrogenIdx == null ? centerIdx : hydrogenIdx.toString();
    vertices.push({ idx: hIdx, pos: V4.scale(bondVectorsSum, -1) });
  }
  vertices.sort((a, b) => parseInt(a.idx, 10) - parseInt(b.idx, 10));

  let lengths = 1;
  for (const { pos } of vertices) {
    lengths *= V4.norm(pos);
  }

  return (
    (+determinant(vertices[1].pos, vertices[2].pos, vertices[3].pos) -
      determinant(vertices[0].pos, vertices[2].pos, vertices[3].pos) +
      determinant(vertices[0].pos, vertices[1].pos, vertices[3].pos) -
      determinant(vertices[0].pos, vertices[1].pos, vertices[2].pos)) /
    lengths
  );
}

function determinant(p0, p1, p2) {
  return (
    p0[0] * p1[1] * p2[2] +
    p0[1] * p1[2] * p2[0] +
    p0[2] * p1[0] * p2[1] -
    p0[2] * p1[1] * p2[0] -
    p0[0] * p1[2] * p2[1] -
    p0[1] * p1[0] * p2[2]
  );
}

export function neighborsChiralityData(mol, atomIdx, positions) {
  debug(tuple(molTest, string, positionsTest), [mol, atomIdx, positions]);

  const result = {};
  for (const [bond, { idx }] of neighborsList(mol, atomIdx)) {
    const type = bond.start === atomIdx ? bond.type : null;
    const pos = positions[idx];
    result[idx] = { pos, type };
  }
  return debug(neighborsChiralityDataTest, result);
}

export function calcDepictedGeometry(mol, bondIdx, positions = mol.positions) {
  debug(tuple(molTest, string, positionsTest), [mol, bondIdx, positions]);

  const bond = mol.bonds[bondIdx];
  const neighbors = atomToNeighborsPairs(mol);
  const aBondCount = neighbors[bond.start].length;
  const bBondCount = neighbors[bond.end].length;
  if (aBondCount > 3 || aBondCount < 2 || bBondCount > 3 || bBondCount < 2) {
    return null;
  }
  const [startAngle1, startAngle2] = geometryAngles(bond.start, bond.end, neighbors, positions);
  const [endAngle1, endAngle2] = geometryAngles(bond.end, bond.start, neighbors, positions);
  return startAngle1 < startAngle2 === endAngle1 > endAngle2 ? Drawing.CIS : Drawing.TRANS;
}

function geometryAngles(startIdx, endIdx, neighbors, positions) {
  const [idx1, idx2] = geometryIndices(neighbors, startIdx, endIdx);
  return [geometryAngle(startIdx, endIdx, idx1, positions), geometryAngle(startIdx, endIdx, idx2, positions)];
}

function geometryAngle(startIdx, endIdx, geomIdx, positions) {
  return geomIdx === startIdx ? 0 : bondAngle(positions, geomIdx, startIdx, endIdx);
}

export function geometryIndices(neighbors, startIdx, endIdx) {
  const startNeighbors = neighbors[startIdx].filter(([b, a]) => a.idx !== endIdx);
  const idx1 = startNeighbors[0][1].idx;
  const idx2 = startNeighbors.length === 1 ? startIdx : startNeighbors[1][1].idx;
  return parseInt(idx1, 10) < parseInt(idx2, 10) ? [idx1, idx2] : [idx2, idx1];
}

function bondAngle(positions, nborIdxA, centerIdx, nborIdxB) {
  const posA = positions[nborIdxA];
  const posB = positions[nborIdxB];
  const pos0 = positions[centerIdx];
  const aVector = V4.sub(posA, pos0);
  const bVector = V4.sub(pos0, posB);
  return V4.angleZ(aVector, bVector);
}
